"use client";

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-hot-toast";

const StudentDashboard = () => {
  const [userName, setUserName] = useState("");
  const [documents, setDocuments] = useState([]);
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("token");
    const role = localStorage.getItem("userRole");
    const name = localStorage.getItem("userName");

    // if (!token || role !== "student") {
    //   navigate("/login");
    //   return;
    // }

    setUserName(name || "Student");
    fetchDocuments();
  }, [navigate]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/documents`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const noticesList = response.data.filter(
        (doc) => doc.document_type === "notice"
      );
      setNotices(noticesList);
      setDocuments(response.data);
      setError("");
    } catch (err) {
      setError("Failed to fetch documents. Please try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (documentId, fileName) => {
    try {
      const token = localStorage.getItem("token");

      // Show download progress
      const progressToast = toast.loading("Downloading file...");

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/documents/download/${documentId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept:
              "application/json, application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, image/jpeg, image/png",
          },
          responseType: "blob",
          onDownloadProgress: (progressEvent) => {
            // Don't try to update the toast with percentages as it's not supported this way
            // Instead, we'll just show a simple loading toast
          },
        }
      );

      // Verify the response
      if (response.data.size === 0) {
        toast.dismiss(progressToast);
        throw new Error("Downloaded file is empty");
      }

      // Get content type from response
      const contentType = response.headers["content-type"];
      if (!contentType) {
        toast.dismiss(progressToast);
        throw new Error("Invalid content type received");
      }

      // Verify content type is valid
      const validTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "image/jpeg",
        "image/png",
      ];

      if (!validTypes.includes(contentType)) {
        toast.dismiss(progressToast);
        throw new Error("Invalid file type received");
      }

      // Get file extension from content type
      const extensionMap = {
        "application/pdf": ".pdf",
        "application/msword": ".doc",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
          ".docx",
        "application/vnd.ms-excel": ".xls",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
          ".xlsx",
        "image/jpeg": ".jpg",
        "image/png": ".png",
      };

      const extension = extensionMap[contentType] || "";
      const fullFileName = fileName + (fileName.includes(".") ? "" : extension);

      // Create a blob with the correct content type
      const blob = new Blob([response.data], { type: contentType });

      // Verify blob size matches Content-Length if provided
      const contentLength = response.headers["content-length"];
      if (contentLength && blob.size !== parseInt(contentLength)) {
        toast.dismiss(progressToast);
        throw new Error("File size mismatch - possible corruption");
      }

      // Create and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fullFileName);
      document.body.appendChild(link);
      link.click();

      // Clean up
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
        toast.dismiss(progressToast);
        toast.success("Download completed successfully!");
      }, 100);
    } catch (error) {
      console.error("Error downloading document:", error);
      toast.error(
        error.message || "Failed to download the document. Please try again."
      );
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("userRole");
    localStorage.removeItem("userName");
    navigate("/portal");
  };

  const getDocumentsByType = (type) => {
    return documents.filter((doc) => doc.document_type === type);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <nav className="bg-blue-600 text-white p-4">
        <div className="container mx-auto flex justify-between items-center">
          <h1 className="text-xl font-bold">NIIT Student Portal</h1>
          <div className="flex items-center space-x-4">
            <button
              onClick={handleLogout}
              className="bg-blue-700 px-4 py-2 rounded hover:bg-blue-800"
            >
              Logout
            </button>
          </div>
        </div>
      </nav>

      <div className="container mx-auto p-6">
        {/* Important Notices Section */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4">Important Notices</h2>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          {loading ? (
            <div className="text-center py-4">Loading...</div>
          ) : notices.length === 0 ? (
            <div className="bg-white p-6 rounded-lg shadow-md text-center text-gray-500">
              No notices available at this time.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {notices.map((notice) => (
                <div
                  key={notice.id}
                  className="bg-white p-6 rounded-lg shadow-md border-l-4 border-blue-500"
                >
                  <h3 className="text-lg font-semibold mb-2">{notice.title}</h3>
                  <p className="text-gray-600 mb-4">{notice.description}</p>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">
                      {new Date(notice.upload_date).toLocaleDateString()}
                    </span>
                    <button
                      onClick={() => handleDownload(notice.id, notice.title)}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      Download
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Documents Section */}
        <h2 className="text-2xl font-bold mb-6">Student Resources</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Timetables */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold mb-3">Timetables</h3>
            <p className="text-gray-600 mb-4">
              Access your class schedules and exam timetables
            </p>

            {loading ? (
              <div className="text-center py-2">Loading...</div>
            ) : getDocumentsByType("timetable").length === 0 ? (
              <p className="text-sm text-gray-500">No timetables available</p>
            ) : (
              <ul className="space-y-2">
                {getDocumentsByType("timetable").map((doc) => (
                  <li key={doc.id} className="border-b pb-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">{doc.title}</span>
                      <button
                        onClick={() => handleDownload(doc.id, doc.title)}
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        Download
                      </button>
                    </div>
                    <p className="text-xs text-gray-500">
                      {new Date(doc.upload_date).toLocaleDateString()}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Syllabus */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold mb-3">Syllabus</h3>
            <p className="text-gray-600 mb-4">
              View course syllabi and learning objectives
            </p>

            {loading ? (
              <div className="text-center py-2">Loading...</div>
            ) : getDocumentsByType("syllabus").length === 0 ? (
              <p className="text-sm text-gray-500">
                No syllabus documents available
              </p>
            ) : (
              <ul className="space-y-2">
                {getDocumentsByType("syllabus").map((doc) => (
                  <li key={doc.id} className="border-b pb-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">{doc.title}</span>
                      <button
                        onClick={() => handleDownload(doc.id, doc.title)}
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        Download
                      </button>
                    </div>
                    <p className="text-xs text-gray-500">
                      {new Date(doc.upload_date).toLocaleDateString()}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Assignments */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold mb-3">Assignments</h3>
            <p className="text-gray-600 mb-4">
              Access your assignments and submission guidelines
            </p>

            {loading ? (
              <div className="text-center py-2">Loading...</div>
            ) : getDocumentsByType("assignment").length === 0 ? (
              <p className="text-sm text-gray-500">No assignments available</p>
            ) : (
              <ul className="space-y-2">
                {getDocumentsByType("assignment").map((doc) => (
                  <li key={doc.id} className="border-b pb-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">{doc.title}</span>
                      <button
                        onClick={() => handleDownload(doc.id, doc.title)}
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        Download
                      </button>
                    </div>
                    <p className="text-xs text-gray-500">
                      {new Date(doc.upload_date).toLocaleDateString()}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Other Documents */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold mb-3">Other Resources</h3>
            <p className="text-gray-600 mb-4">
              Additional learning materials and resources
            </p>

            {loading ? (
              <div className="text-center py-2">Loading...</div>
            ) : getDocumentsByType("other").length === 0 ? (
              <p className="text-sm text-gray-500">
                No additional resources available
              </p>
            ) : (
              <ul className="space-y-2">
                {getDocumentsByType("other").map((doc) => (
                  <li key={doc.id} className="border-b pb-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">{doc.title}</span>
                      <button
                        onClick={() => handleDownload(doc.id, doc.title)}
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        Download
                      </button>
                    </div>
                    <p className="text-xs text-gray-500">
                      {new Date(doc.upload_date).toLocaleDateString()}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* All Documents */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold mb-3">All Documents</h3>
            <p className="text-gray-600 mb-4">View all available documents</p>

            {loading ? (
              <div className="text-center py-2">Loading...</div>
            ) : documents.length === 0 ? (
              <p className="text-sm text-gray-500">No documents available</p>
            ) : (
              <button
                onClick={() => navigate("/student-documents")}
                className="text-blue-600 hover:text-blue-800"
              >
                View All Documents →
              </button>
            )}
          </div>

          {/* Profile */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold mb-3">Profile</h3>
            <p className="text-gray-600 mb-4">
              Update your personal information
            </p>
            <button className="text-blue-600 hover:text-blue-800">
              Edit Profile →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentDashboard;
